export const SIDEBAR_PREFERENCES_STORAGE_KEY = "bs:sidebar-preferences:v2";
const SIDEBAR_PREFERENCES_STORAGE_PREFIX = "bs:sidebar-preferences:v3:";

export const SIDEBAR_MODE_OPTIONS = Object.freeze(["full", "compact", "hidden"]);
export const SIDEBAR_DENSITY_OPTIONS = Object.freeze(["comfortable", "compact"]);

export const DEFAULT_SIDEBAR_PREFERENCES = Object.freeze({
  mode: "full",
  collapsed: false,
  density: "comfortable",
  showSectionLabels: true,
  showBadges: true,
  showSupport: true,
  showUserCard: true,
  hiddenIds: [],
  order: [],
  customItems: [],
  customSections: [],
  sectionAssignments: {},
});

const MAX_CUSTOM_ITEMS = 24;
const MAX_CUSTOM_SECTIONS = 12;
const MAX_SECTION_ASSIGNMENTS = 160;
const MAX_LABEL_LENGTH = 36;
const MAX_SECTION_LABEL_LENGTH = 32;
const MAX_PATH_LENGTH = 220;
const DEFAULT_CUSTOM_SECTION_ID = "custom-section:main";
const DEFAULT_CUSTOM_SECTION_LABEL = "Моё меню";
const CORE_ITEM_KEYS = new Set([
  "dashboard",
  "profile",
  "appointments",
  "calendar",
  "chat",
  "clients",
  "conferences",
  "diary",
  "notifications",
  "settings",
]);

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeStorageScope(value) {
  return cleanString(value, 48).toLowerCase() || "guest";
}

function cleanString(value, maxLength = 120) {
  return String(value || "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .slice(0, maxLength);
}

function slugify(value, fallback = "item") {
  const slug = cleanString(value, 80)
    .toLowerCase()
    .replace(/[^a-z0-9а-яё_-]+/gi, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || fallback;
}

export function normalizeSidebarPath(value) {
  const raw = cleanString(value, MAX_PATH_LENGTH);
  if (!raw) return "";
  if (raw.startsWith("//")) return "";
  if (raw.startsWith("/") || raw.startsWith("#")) return raw;
  return `/${raw.replace(/^\/+/, "")}`;
}

export function normalizeCustomSidebarSection(raw = {}, index = 0) {
  const label = cleanString(raw.label || raw.title || raw.name, MAX_SECTION_LABEL_LENGTH);
  const fallback = `custom-section-${Date.now()}-${index}`;
  const rawId = cleanString(raw.id || raw.sectionId || raw.key || label || fallback, 80);
  const id = rawId.startsWith("custom-section:")
    ? rawId
    : `custom-section:${slugify(rawId || fallback, `section-${index}`)}`;
  if (!label) return null;
  return { id, label };
}

export function normalizeCustomSidebarItem(raw = {}, index = 0) {
  const id = cleanString(raw.id, 64) || `custom-${Date.now()}-${index}`;
  const label = cleanString(raw.label || raw.title || raw.name, MAX_LABEL_LENGTH);
  const to = normalizeSidebarPath(raw.to || raw.path || raw.url);
  const rawSectionId = cleanString(raw.sectionId || raw.groupId || raw.section || raw.group || "", 80);
  const sectionId = rawSectionId
    ? (rawSectionId.startsWith("custom-section:") ? rawSectionId : `custom-section:${slugify(rawSectionId, "main")}`)
    : DEFAULT_CUSTOM_SECTION_ID;
  if (!label || !to) return null;
  return {
    id: id.startsWith("custom:") ? id : `custom:${id.replace(/^custom-?/, "")}`,
    label,
    to,
    sectionId,
  };
}



function normalizeSidebarMode(value, source = {}) {
  const mode = cleanString(value, 24).toLowerCase();
  if (SIDEBAR_MODE_OPTIONS.includes(mode)) return mode;
  if (source.collapsed === true || source.hidden === true || source.sidebarHidden === true) return "hidden";
  if (source.compact === true || source.rail === true || source.iconOnly === true) return "compact";
  return DEFAULT_SIDEBAR_PREFERENCES.mode;
}

function normalizeSidebarDensity(value) {
  const density = cleanString(value, 24).toLowerCase();
  return SIDEBAR_DENSITY_OPTIONS.includes(density) ? density : DEFAULT_SIDEBAR_PREFERENCES.density;
}

function normalizeBool(value, fallback = true) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const raw = String(value ?? "").trim().toLowerCase();
  if (["true", "1", "yes", "on"].includes(raw)) return true;
  if (["false", "0", "no", "off"].includes(raw)) return false;
  return fallback;
}

function normalizeSectionAssignments(value) {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const out = {};
  let count = 0;
  Object.entries(source).forEach(([rawNavId, rawSectionId]) => {
    if (count >= MAX_SECTION_ASSIGNMENTS) return;
    const navId = cleanString(rawNavId, 96);
    const sectionIdRaw = cleanString(rawSectionId, 80);
    if (!navId || !sectionIdRaw) return;
    const sectionId = sectionIdRaw.startsWith("custom-section:")
      ? sectionIdRaw
      : `custom-section:${slugify(sectionIdRaw, "main")}`;
    out[navId] = sectionId;
    count += 1;
  });
  return out;
}

export function normalizeSidebarPreferences(raw = {}) {
  const source = raw && typeof raw === "object" ? raw : {};
  const hiddenIds = Array.from(
    new Set(asArray(source.hiddenIds || source.hidden || source.hiddenNavIds).map((value) => cleanString(value, 96)).filter(Boolean))
  );
  const order = Array.from(
    new Set(asArray(source.order || source.navOrder || source.menuOrder).map((value) => cleanString(value, 96)).filter(Boolean))
  );
  const customSections = asArray(source.customSections || source.sections || source.customGroups || source.groups)
    .map(normalizeCustomSidebarSection)
    .filter(Boolean)
    .slice(0, MAX_CUSTOM_SECTIONS);
  const sectionIds = new Set(customSections.map((section) => section.id));
  const customItems = asArray(source.customItems || source.items || source.customLinks)
    .map(normalizeCustomSidebarItem)
    .filter(Boolean)
    .slice(0, MAX_CUSTOM_ITEMS)
    .map((item) => ({
      ...item,
      sectionId: sectionIds.has(item.sectionId) ? item.sectionId : DEFAULT_CUSTOM_SECTION_ID,
    }));

  const mode = normalizeSidebarMode(source.mode || source.displayMode || source.sidebarMode, source);
  const density = normalizeSidebarDensity(source.density || source.sidebarDensity);

  return {
    mode,
    collapsed: mode === "hidden",
    density,
    showSectionLabels: normalizeBool(source.showSectionLabels ?? source.sectionLabels, true),
    showBadges: normalizeBool(source.showBadges ?? source.badges, true),
    showSupport: normalizeBool(source.showSupport ?? source.support, true),
    showUserCard: normalizeBool(source.showUserCard ?? source.userCard, true),
    hiddenIds,
    order,
    customItems,
    customSections,
    sectionAssignments: normalizeSectionAssignments(source.sectionAssignments || source.itemSections || source.sectionByItem),
  };
}

export function sidebarStorageKey(scope = "guest") {
  return `${SIDEBAR_PREFERENCES_STORAGE_PREFIX}${normalizeStorageScope(scope)}`;
}

export function hasSidebarPreferencesLocal(scope = "guest") {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(sidebarStorageKey(scope)) != null || window.localStorage.getItem(SIDEBAR_PREFERENCES_STORAGE_KEY) != null;
  } catch {
    return false;
  }
}

export function readSidebarPreferencesLocal(scope = "guest") {
  if (typeof window === "undefined") return DEFAULT_SIDEBAR_PREFERENCES;
  try {
    const scopedRaw = window.localStorage.getItem(sidebarStorageKey(scope));
    if (scopedRaw) return normalizeSidebarPreferences(JSON.parse(scopedRaw));
    const legacyRaw = window.localStorage.getItem(SIDEBAR_PREFERENCES_STORAGE_KEY);
    return legacyRaw ? normalizeSidebarPreferences(JSON.parse(legacyRaw)) : DEFAULT_SIDEBAR_PREFERENCES;
  } catch {
    return DEFAULT_SIDEBAR_PREFERENCES;
  }
}

export function writeSidebarPreferencesLocal(scope = "guest", value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      sidebarStorageKey(scope),
      JSON.stringify(normalizeSidebarPreferences(value))
    );
  } catch {}
}

export function sidebarNavId(sectionKey, item = {}) {
  const raw = item.navId || item.id || item.key || item.to || item.label || "";
  const cleaned = cleanString(raw, 80);
  if (cleaned.startsWith(`${sectionKey}:`)) return cleaned;
  const normalized = cleaned.replace(/\s+/g, "-").toLowerCase();
  return `${sectionKey}:${normalized || "item"}`;
}

export function isSidebarItemLocked(sectionKey, item = {}) {
  if (item.locked === true || item.required === true || item.system === true) return true;
  const key = cleanString(item.key || "", 80).toLowerCase();
  if (CORE_ITEM_KEYS.has(key)) return true;
  return sectionKey === "context";
}

export function applySidebarPreferencesToItems(items = [], sectionKey, preferences = {}) {
  const normalized = normalizeSidebarPreferences(preferences);
  const hidden = new Set(normalized.hiddenIds);
  const orderIndex = new Map(normalized.order.map((id, index) => [id, index]));

  return asArray(items)
    .map((item, index) => {
      const navId = sidebarNavId(sectionKey, item);
      const locked = isSidebarItemLocked(sectionKey, item);
      return { ...item, navId, locked, originalIndex: index };
    })
    .filter((item) => item.locked || !hidden.has(item.navId))
    .sort((a, b) => {
      const ai = orderIndex.has(a.navId) ? orderIndex.get(a.navId) : Number.MAX_SAFE_INTEGER;
      const bi = orderIndex.has(b.navId) ? orderIndex.get(b.navId) : Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return ai - bi;
      return Number(a.originalIndex || 0) - Number(b.originalIndex || 0);
    });
}

export function defaultCustomSidebarSection() {
  return { id: DEFAULT_CUSTOM_SECTION_ID, label: DEFAULT_CUSTOM_SECTION_LABEL };
}

export function serializeSidebarPreferencesForApi(value = {}) {
  const normalized = normalizeSidebarPreferences(value);
  return {
    mode: normalized.mode,
    collapsed: normalized.collapsed,
    density: normalized.density,
    showSectionLabels: normalized.showSectionLabels,
    showBadges: normalized.showBadges,
    showSupport: normalized.showSupport,
    showUserCard: normalized.showUserCard,
    hiddenIds: normalized.hiddenIds,
    order: normalized.order,
    customItems: normalized.customItems,
    customSections: normalized.customSections,
    sectionAssignments: normalized.sectionAssignments,
  };
}
